// Thin client for the appointments public booking API.
//
// Flow: bootstrap(slug) -> short-lived guest token (carries the tenantId
// internally) -> every other call rides on that token. The slug never appears
// in any URL after bootstrap, matching the backend's two-tier public scheme.
import { API_BASE_URL, TENANT_SLUG } from '../config';

let bootstrapPromise = null;

/**
 * Exchange the configured tenant slug for a guest token + tenant context
 * (businessType, feature flags). Cached for the session.
 */
function bootstrap() {
  if (!bootstrapPromise) {
    bootstrapPromise = fetch(`${API_BASE_URL}/public/bootstrap`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug: TENANT_SLUG }),
    })
      .then(handleResponse)
      .catch((err) => {
        bootstrapPromise = null; // allow a retry on the next call
        throw err;
      });
  }
  return bootstrapPromise;
}

function getGuestToken() {
  return bootstrap().then((data) => data.token);
}

/** Parse the body and turn RFC-7807 error payloads into readable Error messages. */
async function handleResponse(res) {
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) {
    const detail =
      (body && (body.detail || body.title || body.message)) ||
      `Error ${res.status}`;
    const error = new Error(detail);
    error.status = res.status;
    error.body = body;
    throw error;
  }
  return body;
}

function safeJson(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** Authenticated GET/POST against the public API, re-bootstrapping once on 401. */
async function request(path, { method = 'GET', body, headers = {}, _retried } = {}) {
  const token = await getGuestToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  // Guest token expired (1h TTL) -> drop it and bootstrap again, once.
  if (res.status === 401 && !_retried) {
    bootstrapPromise = null;
    return request(path, { method, body, headers, _retried: true });
  }
  return handleResponse(res);
}

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback for older browsers.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const bookingApi = {
  /** Tenant context from bootstrap: { id, name, timezone, currency, businessType, features }. */
  getContext() {
    return bootstrap().then((b) => b.tenant);
  },

  /** Active branches/locations for the tenant. */
  getBranches() {
    return request('/public/branches');
  },

  /** Service catalog grouped by category, plus tenant display info. */
  getServices() {
    return request('/public/services');
  },

  /** Public staff, optionally scoped to a service and/or branch. */
  getStaff({ serviceId, branchId } = {}) {
    const q = new URLSearchParams();
    if (serviceId) q.set('serviceId', serviceId);
    if (branchId) q.set('branchId', branchId);
    const qs = q.toString();
    return request(`/public/staff${qs ? `?${qs}` : ''}`);
  },

  /** Availability slots (grouped per day: morning/afternoon/evening). */
  getAvailability({ serviceId, branchId, dateFrom, dateTo, staffId, gender, vehicleTypeId, zip }) {
    const q = new URLSearchParams({ serviceId, dateFrom, dateTo });
    if (branchId) q.set('branchId', branchId);
    if (staffId) q.set('staffId', staffId);
    if (gender) q.set('gender', gender);
    if (vehicleTypeId) q.set('vehicleTypeId', vehicleTypeId);
    if (zip) q.set('zip', zip); // requerido por tenants mobile_detailing (mapea zona/ruta)
    return request(`/public/availability?${q.toString()}`);
  },

  /** Create a booking as a guest (no account / email verification). */
  createGuestBooking(payload) {
    return request('/public/bookings/guest', {
      method: 'POST',
      body: payload,
      headers: { 'Idempotency-Key': uuid() },
    });
  },
};
